export function Loader({ label }: { label?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-24">
      <div className="relative flex gap-1.5">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="h-2.5 w-2.5 rounded-full bg-plug/40"
            style={{ animation: `bounce 1.2s ease-in-out ${i * 0.15}s infinite` }}
          />
        ))}
      </div>
      {label && <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-stone">{label}</span>}
      <style>{`
        @keyframes bounce {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
          40% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

export function ProductCardSkeleton() {
  return (
    <article className="pointer-events-none overflow-hidden rounded-2xl border border-clay/40 animate-shimmer">
      <div className="aspect-[4/5] w-full bg-sand" />
      <div className="p-4 space-y-3">
        <div className="h-3 w-3/4 bg-sand rounded" />
        <div className="h-4 w-1/2 bg-sand rounded" />
        <div className="h-5 w-20 bg-sand rounded" />
      </div>
    </article>
  );
}

export function ProductGridSkeleton({ count = 8 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 xl:grid-cols-4" aria-busy="true" aria-label="Loading products">
      {Array.from({ length: count }).map((_, i) => (
        <ProductCardSkeleton key={i} />
      ))}
    </div>
  );
}

export function AdminTableSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="overflow-hidden rounded-xl border border-clay/60 animate-shimmer">
      <table className="w-full text-left text-[13px]">
        <thead className="bg-linen/80 text-[11px] uppercase tracking-wider2 text-stone">
          <tr>
            <th className="px-4 py-3">Item</th>
            <th className="hidden px-4 py-3 md:table-cell">SKU</th>
            <th className="hidden px-4 py-3 md:table-cell">Category</th>
            <th className="px-4 py-3 text-right">Price</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3 text-right">Edit</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-clay/40">
          {Array.from({ length: rows }).map((_, i) => (
            <tr key={i}>
              <td className="flex items-center gap-3 px-4 py-3">
                <div className="aspect-[4/5] w-10 shrink-0 rounded bg-sand" />
                <div className="space-y-2">
                  <div className="h-4 w-32 bg-sand rounded" />
                  <div className="h-3 w-20 bg-sand rounded" />
                </div>
              </td>
              <td className="hidden px-4 py-3 md:table-cell"><div className="h-3 w-16 bg-sand rounded" /></td>
              <td className="hidden px-4 py-3 md:table-cell"><div className="h-3 w-20 bg-sand rounded" /></td>
              <td className="px-4 py-3 text-right"><div className="h-4 w-16 bg-sand rounded ml-auto" /></td>
              <td className="px-4 py-3"><div className="h-4 w-20 bg-sand rounded" /></td>
              <td className="px-4 py-3 text-right"><div className="h-4 w-12 bg-sand rounded ml-auto" /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function EmptyState({ title, body, cta }: { title: string; body?: string; cta?: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
      <div className="grid h-16 w-16 place-items-center rounded-2xl border border-clay/60 bg-sand/50">
        <svg width="28" height="28" viewBox="0 0 24 24" aria-hidden className="text-stone/50">
          <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M12 8v4M12 16v0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </div>
      <div>
        <p className="font-display text-xl font-extrabold text-ink">{title}</p>
        {body && <p className="mt-1 max-w-sm text-[14px] text-slate">{body}</p>}
      </div>
      {cta}
    </div>
  );
}

export function ErrorState({ message }: { message: string }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-wine/20 bg-wine/5 px-4 py-3 text-[14px] text-wine">
      <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden className="shrink-0">
        <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.4" />
        <path d="M8 5v3.5M8 11v.01" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
      {message}
    </div>
  );
}
